import { Injectable } from '@angular/core';
import { Observable, of, Subject, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { lcApiBase, readObservableError } from './logiciel-caisse-api.helper';
import { LogicielCaisseAuthService } from './logiciel-caisse-auth.service';

export interface ReasortTransfertCounts {
  en_cours: number;
  clos: number;
}

export interface ReasortTransfertTableRow {
  id: number;
  'N° DOCUMENT': string;
  'DATE TRANSFERT': string;
  'QUANTITÉ': string;
  DÉPART: string;
  RÉCEPTION: string;
  STATUT: string;
  NOTE: string;
  _is_reception?: number;
}

/** Statuts stock_transfert (backend-logiciel-caisse). */
export const REASORT_IS = {
  DEFAUT: 0,
  RECEPTION_TERMINEE: 1,
  /** Créé / en cours de transfert — archivable. */
  CREE: 2,
  EN_COURS_TRANSFERT: 2,
  ANNULE: 3,
  EN_COURS_LIVRAISON: 4,
} as const;

export interface ReasortTransfertDetailBarcode {
  product_id: number | null;
  code_barre: string;
  quantite: number;
  couleur?: string;
  taille?: string;
}

export interface ReasortTransfertDetailLigne {
  libelle_article: string;
  reference: string;
  barcodes: ReasortTransfertDetailBarcode[];
}

export interface ReasortTransfertDetail {
  id: number;
  num_document: string;
  date_transfert: string;
  entrepot_id_depart: number;
  entrepot_id_reception: number;
  note: string | null;
  is_reception: number;
  quantite?: number | null;
  lignes: ReasortTransfertDetailLigne[];
}

export interface ReasortTestAutoResult {
  created: Array<{
    id: number;
    num_document?: string;
    entrepot_id_depart: number;
    entrepot_id_reception: number;
    lignes: number;
  }>;
  summary: {
    message: string;
    transfers: number;
    lines: number;
  };
  warnings: string[];
}

/**
 * Réassort achat → API logiciel-caisse (`/api/stock-transferts`, origine=reasort).
 */
@Injectable({ providedIn: 'root' })
export class LogicielCaisseReasortTransfertService {
  private readonly listRefresh$ = new Subject<void>();

  constructor(
    private readonly http: HttpClient,
    private readonly authService: LogicielCaisseAuthService
  ) {}

  onListRefresh(): Observable<void> {
    return this.listRefresh$.asObservable();
  }

  notifyListRefresh(): void {
    this.listRefresh$.next();
  }

  private baseUrl(): string {
    return `${lcApiBase(this.authService)}/api/stock-transferts`;
  }

  /**
   * Contexte métier stock_transfert : JWT (interceptor) + repli body user/station.
   */
  private buildTransfertContextBody(): Record<string, number> {
    const session = this.authService.getSession();
    const fromJwt = this.decodeLcJwt(this.authService.getLogicielCaisseToken());
    const user_id =
      Number(session?.userId) > 0
        ? Number(session!.userId)
        : Number(fromJwt?.id) > 0
          ? Number(fromJwt!.id)
          : Number(session?.erpUserId) > 0
            ? Number(session!.erpUserId)
            : 0;
    const station_id =
      Number(session?.station_id_back ?? session?.stationId) > 0
        ? Number(session!.station_id_back ?? session!.stationId)
        : Number(fromJwt?.station_id) > 0
          ? Number(fromJwt!.station_id)
          : 0;

    const body: Record<string, number> = {};
    if (user_id > 0) {
      body.user_id = user_id;
    }
    if (station_id > 0) {
      body.station_id = station_id;
    }
    return body;
  }

  private assertCanCallTransfertApi(body: Record<string, number>): void {
    const token = this.authService.getLogicielCaisseToken();
    if (token?.trim()) {
      return;
    }
    if (body.user_id > 0 && body.station_id > 0) {
      return;
    }
    throw new Error(
      'Session logiciel caisse incomplète (user/station). Reconnectez le module caisse puis relancez.'
    );
  }

  private decodeLcJwt(token: string | null | undefined): { id?: number; station_id?: number } | null {
    if (!token?.trim()) {
      return null;
    }
    try {
      const parts = token.trim().split('.');
      if (parts.length < 2) {
        return null;
      }
      const json = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'));
      const payload = JSON.parse(json) as {
        id?: number;
        user_id?: number;
        station_id?: number;
        type?: string;
      };
      const id =
        payload.type === 'gerant' && payload.user_id != null
          ? Number(payload.user_id)
          : Number(payload.id);
      const station_id = Number(payload.station_id);
      return {
        id: Number.isFinite(id) && id > 0 ? id : undefined,
        station_id: Number.isFinite(station_id) && station_id > 0 ? station_id : undefined,
      };
    } catch {
      return null;
    }
  }

  private withContextParams(base?: HttpParams): HttpParams {
    const ctx = this.buildTransfertContextBody();
    this.assertCanCallTransfertApi(ctx);
    let params = base ?? new HttpParams();
    if (ctx.user_id > 0) {
      params = params.set('user_id', String(ctx.user_id));
    }
    if (ctx.station_id > 0) {
      params = params.set('station_id', String(ctx.station_id));
    }
    return params;
  }

  private mapLcError(err: unknown, fallback: string): Error {
    if (err instanceof Error && !(err instanceof HttpErrorResponse)) {
      const msg = String(err.message || '').trim();
      if (msg) {
        return err;
      }
    }
    return new Error(readObservableError(err, fallback));
  }

  /** Assure une session caisse avant les appels API. */
  ensureCaisseSession(): Observable<boolean> {
    if (this.authService.isAuthenticated()) {
      return of(true);
    }
    return this.authService.bootstrap().pipe(
      map((status) => status === 'ready' && this.authService.isAuthenticated()),
      catchError(() => throwError(new Error('Session logiciel caisse indisponible.')))
    );
  }

  getCounts(): Observable<ReasortTransfertCounts> {
    return this.ensureCaisseSession().pipe(
      switchMap((ok) => {
        if (!ok) {
          return throwError(new Error('Connectez le module logiciel caisse pour le réassort.'));
        }
        const params = this.withContextParams(new HttpParams().set('origine', 'reasort'));
        return this.http
          .get<{
            success: boolean;
            counts?: { en_cours?: number; clos?: number };
            error?: string;
          }>(`${this.baseUrl()}/counts`, { params })
          .pipe(
            map((res) => {
              if (!res?.success || !res.counts) {
                throw new Error(res?.error || 'Réponse compteurs invalide');
              }
              return {
                en_cours: Number(res.counts.en_cours) || 0,
                clos: Number(res.counts.clos) || 0,
              };
            })
          );
      }),
      catchError((err) => throwError(this.mapLcError(err, 'Erreur compteurs réassort.')))
    );
  }

  list(params: {
    tab: 'en_cours' | 'clos';
    page: number;
    limit: number;
    date_debut?: string;
    date_fin?: string;
    mot_cle?: string;
  }): Observable<{
    items: ReasortTransfertTableRow[];
    pagination: { total: number; page: number; limit: number };
  }> {
    return this.ensureCaisseSession().pipe(
      switchMap((ok) => {
        if (!ok) {
          return throwError(new Error('Connectez le module logiciel caisse pour le réassort.'));
        }
        let httpParams = this.withContextParams(
          new HttpParams()
            .set('tab', params.tab)
            .set('page', String(params.page))
            .set('limit', String(params.limit))
            .set('origine', 'reasort')
        );
        if (params.date_debut?.trim()) {
          httpParams = httpParams.set('date_debut', params.date_debut.trim());
        }
        if (params.date_fin?.trim()) {
          httpParams = httpParams.set('date_fin', params.date_fin.trim());
        }
        if (params.mot_cle?.trim()) {
          httpParams = httpParams.set('mot_cle', params.mot_cle.trim());
        }
        return this.http
          .get<{
            success: boolean;
            items?: ReasortTransfertTableRow[];
            pagination?: { total: number; page: number; limit: number };
            error?: string;
          }>(this.baseUrl(), { params: httpParams })
          .pipe(
            map((res) => {
              if (!res?.success || !Array.isArray(res.items) || !res.pagination) {
                throw new Error(res?.error || 'Réponse liste invalide');
              }
              return { items: res.items, pagination: res.pagination };
            })
          );
      }),
      catchError((err) => throwError(this.mapLcError(err, 'Erreur chargement réassort.')))
    );
  }

  /**
   * Lance le moteur auto-réassort (min/max + priorités) :
   * crée des bons de transfert `origine=reasort` au statut Créé (en attente d’approbation).
   */
  lancerReassortTest(): Observable<ReasortTestAutoResult> {
    return this.ensureCaisseSession().pipe(
      switchMap((ok) => {
        if (!ok) {
          return throwError(new Error('Connectez le module logiciel caisse pour le réassort.'));
        }
        const body = this.buildTransfertContextBody();
        this.assertCanCallTransfertApi(body);
        return this.http
          .post<{
            success: boolean;
            created?: ReasortTestAutoResult['created'];
            summary?: ReasortTestAutoResult['summary'];
            warnings?: string[];
            error?: string;
          }>(`${this.baseUrl()}/reasort-test-auto`, body)
          .pipe(
            map((res) => {
              if (!res?.success) {
                throw new Error(res?.error || 'Lancement réassort refusé');
              }
              return {
                created: res.created ?? [],
                summary: res.summary ?? { message: '', transfers: 0, lines: 0 },
                warnings: res.warnings ?? [],
              };
            })
          );
      }),
      catchError((err) => throwError(this.mapLcError(err, 'Échec du lancement réassort')))
    );
  }

  /**
   * Approuve un BT réassort (Créé → En cours de livraison) :
   * en attente de confirmation réception côté caisse pour alimenter le stock.
   */
  approuverTransfert(id: number): Observable<void> {
    return this.patchIsReception(id, REASORT_IS.EN_COURS_LIVRAISON, 'Échec de l’approbation réassort');
  }

  /** Confirme la réception (→ Reçu) et alimente le stock destination. */
  confirmerReception(id: number): Observable<void> {
    return this.patchIsReception(id, REASORT_IS.RECEPTION_TERMINEE, 'Échec de la confirmation de réception');
  }

  /**
   * Archive un BT réassort non approuvé (Créé → Annulé/archivé).
   * Refusé par l’API si déjà en livraison ou réceptionné.
   */
  archiverTransfert(id: number): Observable<void> {
    return this.patchIsReception(id, REASORT_IS.ANNULE, 'Échec de l’archivage réassort');
  }

  getById(id: number): Observable<ReasortTransfertDetail> {
    return this.ensureCaisseSession().pipe(
      switchMap((ok) => {
        if (!ok) {
          return throwError(new Error('Connectez le module logiciel caisse pour le réassort.'));
        }
        const params = this.withContextParams();
        return this.http
          .get<{ success: boolean; detail?: ReasortTransfertDetail; error?: string }>(
            `${this.baseUrl()}/${id}`,
            { params }
          )
          .pipe(
            map((res) => {
              if (!res?.success || !res.detail) {
                throw new Error(res?.error || 'Transfert réassort introuvable');
              }
              return res.detail;
            })
          );
      }),
      catchError((err) => throwError(this.mapLcError(err, 'Erreur chargement détail réassort.')))
    );
  }

  private patchIsReception(id: number, is_reception: number, fallback: string): Observable<void> {
    return this.ensureCaisseSession().pipe(
      switchMap((ok) => {
        if (!ok) {
          return throwError(new Error('Connectez le module logiciel caisse pour le réassort.'));
        }
        const ctx = this.buildTransfertContextBody();
        this.assertCanCallTransfertApi(ctx);
        const body = {
          ...ctx,
          is_reception,
        };
        return this.http
          .patch<{ success: boolean; error?: string }>(`${this.baseUrl()}/${id}`, body)
          .pipe(
            map((res) => {
              if (!res?.success) {
                throw new Error(res?.error || 'Mise à jour refusée');
              }
            })
          );
      }),
      catchError((err) => throwError(this.mapLcError(err, fallback)))
    );
  }
}
